import { useState, useMemo } from 'react';
import { useStats, type LoadedArea } from '@/api/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { GlobeSpinner } from '@/components/ui/globe-spinner';
import { PageBanner } from '@/components/layout/PageBanner';
import { SortableHeader } from '@/components/ui/sortable-header';
import { TablePager } from '@/components/ui/table-pager';
import { StatusDonut } from '@/components/charts/StatusDonut';
import { TopAreasBar } from '@/components/charts/TopAreasBar';
import { useClientSort } from '@/hooks/use-client-sort';
import { formatNumber, timeAgo } from '@/lib/utils';
import { Database, MapPin, Activity, Globe } from 'lucide-react';

const statusVariant = (status: string): 'success' | 'info' | 'warning' | 'destructive' | 'secondary' => {
  const map: Record<string, 'success' | 'info' | 'warning' | 'destructive' | 'secondary'> = {
    active: 'success', completed: 'success', running: 'info',
    pending: 'warning', failed: 'destructive', disabled: 'secondary',
  };
  return map[status] ?? 'secondary';
};

const statusDot = (status: string) => (
  <Badge variant={statusVariant(status)} className="text-xs capitalize">{status}</Badge>
);

const formatVariant = (fmt: string) => {
  const map: Record<string, string> = {
    arcgis_rest: 'orange', socrata_api: 'lime', shapefile_download: 'cyan',
    csv: 'fuchsia', json_api: 'pink', wfs_geojson: 'violet',
  };
  return (map[fmt] ?? 'secondary') as never;
};

const PAGE_SIZE = 15;

const SORT_FIELDS: Record<string, (a: LoadedArea) => string | number | null | undefined> = {
  country: a => a.country || 'US',
  area: a => a.county_name || '',
  type: a => a.format || '',
  status: a => a.status || '',
  parcels: a => a.actual_count || a.parcel_count || 0,
  date: a => a.last_success_at || '',
};

export default function DashboardPage() {
  const { data, isLoading } = useStats();
  const [filter, setFilter] = useState('');

  const filterFn = useMemo(() => {
    if (!filter) return undefined;
    const f = filter.toLowerCase();
    return (a: LoadedArea) =>
      a.county_name?.toLowerCase().includes(f) ||
      a.state_abbr?.trim().toLowerCase().includes(f) ||
      (a.country || 'US').toLowerCase().includes(f);
  }, [filter]);

  const { paged, page, setPage, total, sortField, sortDir, toggleSort } = useClientSort<LoadedArea>({
    data: data?.loadedAreas,
    defaultField: 'parcels',
    defaultDir: 'desc',
    fields: SORT_FIELDS,
    pageSize: PAGE_SIZE,
    filter: filterFn,
  });

  // Compute max parcel count for inline bars
  const maxParcels = useMemo(() => {
    if (!data?.loadedAreas) return 1;
    return Math.max(1, ...data.loadedAreas.map(a => a.actual_count || a.parcel_count || 0));
  }, [data?.loadedAreas]);

  // Top 10 areas for bar chart
  const topAreas = useMemo(() => {
    if (!data?.loadedAreas) return [];
    return [...data.loadedAreas]
      .sort((a, b) => (b.actual_count || b.parcel_count || 0) - (a.actual_count || a.parcel_count || 0))
      .slice(0, 10)
      .map(a => ({
        name: `${a.state_abbr?.trim() || ''} / ${a.county_name || ''}`.trim(),
        parcels: a.actual_count || a.parcel_count || 0,
      }));
  }, [data?.loadedAreas]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <GlobeSpinner size="lg" label="Loading global property data..." />
      </div>
    );
  }

  const activeCount = data?.sourcesByStatus.find(s => s.status === 'active')?.count ?? 0;

  return (
    <div className="space-y-6 bg-gradient-mesh min-h-full p-1">
      <PageBanner>
        <div className="banner-chip">
          <h1 className="text-2xl font-bold banner-title">Dashboard</h1>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="banner-chip flex-col !items-start !gap-1 animate-scale-in stagger-1 opacity-0">
          <div className="flex items-center justify-between w-full">
            <span className="text-xs font-medium text-muted-foreground">Total Parcels</span>
            <div className="p-1.5 rounded-md bg-gradient-to-br from-blue-500 to-cyan-400">
              <MapPin className="h-3 w-3 text-white" />
            </div>
          </div>
          <div className="text-2xl font-bold">{formatNumber(data?.totalParcels)}</div>
          <p className="text-xs text-muted-foreground">across {data?.countryCount ?? 0} countries</p>
        </div>

        <div className="banner-chip flex-col !items-start !gap-1 animate-scale-in stagger-2 opacity-0">
          <div className="flex items-center justify-between w-full">
            <span className="text-xs font-medium text-muted-foreground">Active Sources</span>
            <div className="p-1.5 rounded-md bg-gradient-to-br from-violet-500 to-blue-500">
              <Database className="h-3 w-3 text-white" />
            </div>
          </div>
          <div className="text-2xl font-bold">{activeCount}</div>
          <p className="text-xs text-muted-foreground">{data?.totalSources} total configured</p>
        </div>

        <div className="banner-chip flex-col !items-start !gap-1 animate-scale-in stagger-3 opacity-0">
          <div className="flex items-center justify-between w-full">
            <span className="text-xs font-medium text-muted-foreground">Countries</span>
            <div className="p-1.5 rounded-md bg-gradient-to-br from-cyan-500 to-emerald-400">
              <Globe className="h-3 w-3 text-white" />
            </div>
          </div>
          <div className="text-2xl font-bold">{data?.countryCount ?? 0}</div>
          <p className="text-xs text-muted-foreground">global coverage</p>
        </div>

        <div className="banner-chip flex-col !items-start !gap-1 animate-scale-in stagger-4 opacity-0">
          <div className="flex items-center justify-between w-full">
            <span className="text-xs font-medium text-muted-foreground">Source Status</span>
            <div className="p-1.5 rounded-md bg-gradient-to-br from-amber-500 to-orange-400">
              <Activity className="h-3 w-3 text-white" />
            </div>
          </div>
          <div className="flex gap-3 flex-wrap">
            {data?.sourcesByStatus.map(s => (
              <div key={s.status} className="flex items-center gap-1.5">
                {statusDot(s.status)}
                <span className="text-sm font-mono font-semibold">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
        </div>
      </PageBanner>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="animate-fade-up" style={{ animationDelay: '0.15s', opacity: 0 }}>
          <CardHeader>
            <CardTitle className="text-lg">Top Areas by Parcel Count</CardTitle>
          </CardHeader>
          <CardContent>
            {topAreas.length > 0 ? (
              <TopAreasBar data={topAreas} />
            ) : (
              <p className="text-sm text-muted-foreground py-8 text-center">No data yet</p>
            )}
          </CardContent>
        </Card>

        <Card className="animate-fade-up" style={{ animationDelay: '0.2s', opacity: 0 }}>
          <CardHeader>
            <CardTitle className="text-lg">Sources by Status</CardTitle>
          </CardHeader>
          <CardContent>
            {data?.sourcesByStatus && data.sourcesByStatus.length > 0 ? (
              <StatusDonut data={data.sourcesByStatus} />
            ) : (
              <p className="text-sm text-muted-foreground py-8 text-center">No data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Loaded Areas Table */}
      {data?.loadedAreas && data.loadedAreas.length > 0 && (
        <Card className="animate-fade-up" style={{ animationDelay: '0.25s', opacity: 0 }}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Loaded Areas ({total})</CardTitle>
            <Input
              placeholder="Filter by name, state, country..."
              value={filter}
              onChange={e => { setFilter(e.target.value); setPage(1); }}
              className="w-64"
            />
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <SortableHeader label="Country" field="country" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                  </TableHead>
                  <TableHead>
                    <SortableHeader label="Area" field="area" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                  </TableHead>
                  <TableHead>
                    <SortableHeader label="Type" field="type" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                  </TableHead>
                  <TableHead>
                    <SortableHeader label="Status" field="status" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                  </TableHead>
                  <TableHead className="text-right">
                    <SortableHeader label="Parcels" field="parcels" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                  </TableHead>
                  <TableHead>
                    <SortableHeader label="Last Download" field="date" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paged.map((area: LoadedArea, idx: number) => {
                  const count = area.actual_count || area.parcel_count || 0;
                  const barWidth = Math.max(2, (count / maxParcels) * 100);
                  return (
                    <TableRow key={area.id} className={idx % 2 === 0 ? '' : 'bg-table-row-even'}>
                      <TableCell className="font-mono text-xs">{area.country || 'US'}</TableCell>
                      <TableCell className="font-medium">{area.state_abbr?.trim()} / {area.county_name}</TableCell>
                      <TableCell><Badge variant={formatVariant(area.format)} className="text-xs font-mono">{area.format}</Badge></TableCell>
                      <TableCell>{statusDot(area.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 h-1.5 rounded-full bg-muted overflow-hidden hidden sm:block">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-400"
                              style={{ width: `${barWidth}%` }}
                            />
                          </div>
                          <span className="font-mono text-sm">{formatNumber(count)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">{area.last_success_at ? timeAgo(area.last_success_at) : '-'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <TablePager page={page} onPageChange={setPage} total={total} pageSize={PAGE_SIZE} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
